(() => {
  const OPEN_BODY_CLASS = 'ym-media-inspector-open';
  const MODAL_CLASS = 'ym-media-inspector';
  const MAX_SCALE = 4;
  const DOUBLE_TAP_DELAY = 320;
  const SWIPE_THRESHOLD = 56;

  const strings = {
    dialog: 'Product image viewer',
    previous: 'Previous product image',
    next: 'Next product image',
    itemHint: 'Item Photos – Tap to zoom',
    pinchHint: 'Pinch to zoom',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    resetZoom: 'Reset zoom',
    keyboardHint: 'Use Left and Right to change photos, Plus and Minus to zoom, 0 to reset, and Shift with the arrow keys to pan while zoomed.',
    ...(window.YM_MEDIA_INSPECTOR_STRINGS || {}),
  };

  const modalStates = new WeakMap();

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

  const distance = (pointA, pointB) =>
    Math.hypot(pointB.clientX - pointA.clientX, pointB.clientY - pointA.clientY);

  const midpoint = (pointA, pointB) => ({
    clientX: (pointA.clientX + pointB.clientX) / 2,
    clientY: (pointA.clientY + pointB.clientY) / 2,
  });

  const magnifierIcon = () => `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="10.75" cy="10.75" r="6.75"></circle>
      <path d="m16 16 4.25 4.25"></path>
      <path d="M10.75 7.75v6M7.75 10.75h6"></path>
    </svg>
  `;

  const arrowIcon = (direction) => `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="${direction === 'previous' ? 'm15 5-7 7 7 7' : 'm9 5 7 7-7 7'}"></path>
    </svg>
  `;

  const createButton = (className, label, icon) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.setAttribute('aria-label', label);
    button.innerHTML = icon;
    return button;
  };

  const getSectionSuffix = (modal) => modal.id.replace(/^ProductModal-/, '');

  const getGallery = (modal) => document.getElementById(`MediaGallery-${getSectionSuffix(modal)}`);

  const getModalImages = (content) =>
    Array.from(content.children).filter((element) => element.matches('img[data-media-id]'));

  const getImages = (modal, content) => {
    const gallery = getGallery(modal);
    if (!gallery) return [];

    const modalImagesById = new Map(
      getModalImages(content).map((image) => [image.dataset.mediaId, image]),
    );
    const seenMediaIds = new Set();

    return Array.from(gallery.querySelectorAll('.product__modal-opener--image'))
      .filter((opener) => {
        const mediaItem = opener.closest('.product__media-item');
        return (
          mediaItem &&
          !mediaItem.hidden &&
          mediaItem.getAttribute('aria-hidden') !== 'true'
        );
      })
      .map((opener) => opener.querySelector('.product__media-toggle[data-media-id]')?.dataset.mediaId)
      .filter((mediaId) => {
        if (!mediaId || seenMediaIds.has(mediaId) || !modalImagesById.has(mediaId)) return false;
        seenMediaIds.add(mediaId);
        return true;
      })
      .map((mediaId) => modalImagesById.get(mediaId));
  };

  const getActiveImage = (state) => state.images[state.index] || null;

  const imageCandidates = (image) =>
    (image.getAttribute('srcset') || '')
      .split(',')
      .map((candidate) => candidate.trim().split(/\s+/))
      .filter(([, descriptor]) => descriptor?.endsWith('w'))
      .map(([url, descriptor]) => ({
        url,
        absoluteUrl: (() => {
          try {
            return new URL(url, document.baseURI).href;
          } catch (_error) {
            return url;
          }
        })(),
        width: Number.parseInt(descriptor, 10) || 0,
      }))
      .filter((candidate) => candidate.url && candidate.width > 0)
      .sort((candidateA, candidateB) => candidateA.width - candidateB.width);

  const currentCandidateWidth = (image, candidates) => {
    const currentUrl = image.currentSrc || image.src;
    let absoluteCurrentUrl = currentUrl;

    try {
      absoluteCurrentUrl = new URL(currentUrl, document.baseURI).href;
    } catch (_error) {
      absoluteCurrentUrl = currentUrl;
    }

    const matchingCandidate = candidates.find((candidate) => candidate.absoluteUrl === absoluteCurrentUrl);
    if (matchingCandidate) return matchingCandidate.width;

    try {
      return Number.parseInt(new URL(absoluteCurrentUrl).searchParams.get('width'), 10) || 0;
    } catch (_error) {
      return 0;
    }
  };

  const restoreImageSizes = (state) => {
    state.sizedImages.forEach((image) => {
      if (!state.originalSizes.has(image)) return;

      const originalSizes = state.originalSizes.get(image);
      if (originalSizes === null) image.removeAttribute('sizes');
      else image.setAttribute('sizes', originalSizes);
      state.originalSizes.delete(image);
    });

    state.sizedImages.clear();
    state.requestedCandidateWidths = new WeakMap();
  };

  const requestSharperCandidate = (state) => {
    const image = getActiveImage(state);
    if (!image || !image.clientWidth) return;

    const candidates = imageCandidates(image);
    if (!candidates.length) return;

    const pixelRatio = Math.max(window.devicePixelRatio || 1, 1);
    const requiredSourceWidth = Math.ceil(image.clientWidth * state.scale * pixelRatio);
    const targetCandidate =
      candidates.find((candidate) => candidate.width >= requiredSourceWidth) || candidates[candidates.length - 1];
    const baselineWidth =
      state.requestedCandidateWidths.get(image) ||
      currentCandidateWidth(image, candidates) ||
      (candidates.find((candidate) => candidate.width >= image.clientWidth * pixelRatio) || candidates[0]).width;

    if (targetCandidate.width <= baselineWidth) {
      if (!state.requestedCandidateWidths.has(image)) {
        state.requestedCandidateWidths.set(image, baselineWidth);
      }
      return;
    }

    if (!state.originalSizes.has(image)) {
      state.originalSizes.set(image, image.getAttribute('sizes'));
    }

    state.requestedCandidateWidths.set(image, targetCandidate.width);
    state.sizedImages.add(image);
    image.setAttribute('sizes', `${Math.max(1, Math.ceil(targetCandidate.width / pixelRatio))}px`);
  };

  const clampPan = (state) => {
    const image = getActiveImage(state);
    if (!image) return;

    const viewportWidth = state.content.clientWidth;
    const viewportHeight = state.content.clientHeight;
    const scaledWidth = image.clientWidth * state.scale;
    const scaledHeight = image.clientHeight * state.scale;
    const maximumX = Math.max(0, (scaledWidth - viewportWidth) / 2);
    const maximumY = Math.max(0, (scaledHeight - viewportHeight) / 2);

    state.translateX = clamp(state.translateX, -maximumX, maximumX);
    state.translateY = clamp(state.translateY, -maximumY, maximumY);
  };

  const updateZoomControls = (state) => {
    if (!state.zoomInButton) return;

    const percentage = `${Math.round(state.scale * 100)}%`;
    if (state.zoomResetButton.textContent !== percentage) {
      state.zoomResetButton.textContent = percentage;
    }
    state.zoomOutButton.setAttribute('aria-disabled', state.scale <= 1.01 ? 'true' : 'false');
    state.zoomResetButton.setAttribute('aria-disabled', state.scale <= 1.01 ? 'true' : 'false');
    state.zoomInButton.setAttribute('aria-disabled', state.scale >= MAX_SCALE - 0.01 ? 'true' : 'false');
  };

  const setZoomControlsEnabled = (state, enabled) => {
    if (!state.zoomInButton) return;
    state.zoomOutButton.disabled = !enabled;
    state.zoomResetButton.disabled = !enabled;
    state.zoomInButton.disabled = !enabled;
  };

  const applyTransform = (state) => {
    const image = getActiveImage(state);
    if (!image) return;

    clampPan(state);
    image.style.transform = `translate3d(${state.translateX}px, ${state.translateY}px, 0) scale(${state.scale})`;
    state.modal.classList.toggle('ym-media-inspector--zoomed', state.scale > 1.01);
    updateZoomControls(state);
  };

  const resetTransform = (state) => {
    state.scale = 1;
    state.translateX = 0;
    state.translateY = 0;
    state.points.clear();
    state.dragStart = null;
    state.pinchStart = null;
    state.wasPinching = false;

    state.images.forEach((image) => {
      image.style.transform = '';
    });

    state.modal.classList.remove('ym-media-inspector--zoomed');
    updateZoomControls(state);
  };

  const updateCounter = (state) => {
    state.counter.textContent = `${state.index + 1} / ${state.images.length}`;
    const activeImage = getActiveImage(state);
    const imageDescription = activeImage?.alt ? `: ${activeImage.alt}` : '';
    state.liveStatus.textContent = `Image ${state.index + 1} of ${state.images.length}${imageDescription}`;
    const hasMultipleImages = state.images.length > 1;
    state.previousButton.hidden = !hasMultipleImages;
    state.nextButton.hidden = !hasMultipleImages;
    state.previousButton.disabled = !hasMultipleImages;
    state.nextButton.disabled = !hasMultipleImages;
  };

  const refreshImages = (state, preferredMediaId = null) => {
    const currentMediaId = preferredMediaId || getActiveImage(state)?.dataset.mediaId;
    const refreshedImages = getImages(state.modal, state.content);
    if (!refreshedImages.length) return false;

    state.images = refreshedImages;
    const preferredIndex = currentMediaId
      ? refreshedImages.findIndex((image) => image.dataset.mediaId === currentMediaId)
      : -1;
    state.index = preferredIndex >= 0 ? preferredIndex : clamp(state.index, 0, refreshedImages.length - 1);

    if (state.counter) updateCounter(state);
    return true;
  };

  const showImage = (state, requestedIndex) => {
    if (!state.images.length) return;

    if (state.imageFrame !== null) {
      window.cancelAnimationFrame(state.imageFrame);
      state.imageFrame = null;
    }

    const nextIndex = (requestedIndex + state.images.length) % state.images.length;
    state.images.forEach((image, index) => {
      image.classList.toggle('active', index === nextIndex);
      image.setAttribute('aria-hidden', index === nextIndex ? 'false' : 'true');
    });

    state.index = nextIndex;
    resetTransform(state);
    state.lastTap = { time: 0, clientX: 0, clientY: 0 };
    state.content.scrollTop = 0;
    state.content.scrollLeft = 0;
    updateCounter(state);

    const scheduledImage = getActiveImage(state);
    state.imageFrame = window.requestAnimationFrame(() => {
      state.imageFrame = null;
      if (!state.active || !state.modal.hasAttribute('open') || getActiveImage(state) !== scheduledImage) return;
      applyTransform(state);
      requestSharperCandidate(state);
    });
  };

  const toggleZoomAt = (state, clientX, clientY) => {
    if (state.scale > 1.01) {
      resetTransform(state);
      applyTransform(state);
      requestSharperCandidate(state);
      state.liveStatus.textContent = 'Zoom 100%';
      return;
    }

    const contentRect = state.content.getBoundingClientRect();
    const offsetX = clientX - (contentRect.left + contentRect.width / 2);
    const offsetY = clientY - (contentRect.top + contentRect.height / 2);

    state.scale = 2.5;
    state.translateX = -offsetX * (state.scale - 1);
    state.translateY = -offsetY * (state.scale - 1);
    applyTransform(state);
    requestSharperCandidate(state);
    state.liveStatus.textContent = `Zoom ${Math.round(state.scale * 100)}%`;
  };

  const setKeyboardZoom = (state, requestedScale) => {
    const nextScale = clamp(requestedScale, 1, MAX_SCALE);

    if (nextScale <= 1.01) {
      resetTransform(state);
      applyTransform(state);
      state.liveStatus.textContent = 'Zoom 100%';
      return;
    }

    const scaleRatio = nextScale / Math.max(state.scale, 1);
    state.translateX *= scaleRatio;
    state.translateY *= scaleRatio;
    state.scale = nextScale;
    applyTransform(state);
    requestSharperCandidate(state);
    state.liveStatus.textContent = `Zoom ${Math.round(state.scale * 100)}%`;
  };

  const pointerDown = (state, event) => {
    if (!state.active || event.button > 0) return;

    event.preventDefault();
    state.content.setPointerCapture?.(event.pointerId);
    state.points.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
      pointerType: event.pointerType,
    });

    if (state.points.size === 1) {
      state.dragStart = {
        clientX: event.clientX,
        clientY: event.clientY,
        translateX: state.translateX,
        translateY: state.translateY,
        time: Date.now(),
      };
    }

    if (state.points.size === 2) {
      const [pointA, pointB] = Array.from(state.points.values());
      const center = midpoint(pointA, pointB);
      const contentRect = state.content.getBoundingClientRect();
      const contentCenterX = contentRect.left + contentRect.width / 2;
      const contentCenterY = contentRect.top + contentRect.height / 2;
      state.pinchStart = {
        distance: Math.max(distance(pointA, pointB), 1),
        scale: state.scale,
        anchorX: (center.clientX - contentCenterX - state.translateX) / state.scale,
        anchorY: (center.clientY - contentCenterY - state.translateY) / state.scale,
      };
      state.wasPinching = true;
    }
  };

  const pointerMove = (state, event) => {
    if (!state.active || !state.points.has(event.pointerId)) return;

    event.preventDefault();
    state.points.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
      pointerType: event.pointerType,
    });

    if (state.points.size === 2 && state.pinchStart) {
      const [pointA, pointB] = Array.from(state.points.values());
      const currentCenter = midpoint(pointA, pointB);
      state.scale = clamp(
        state.pinchStart.scale * (distance(pointA, pointB) / state.pinchStart.distance),
        1,
        MAX_SCALE,
      );
      const contentRect = state.content.getBoundingClientRect();
      const contentCenterX = contentRect.left + contentRect.width / 2;
      const contentCenterY = contentRect.top + contentRect.height / 2;
      state.translateX = currentCenter.clientX - contentCenterX - state.pinchStart.anchorX * state.scale;
      state.translateY = currentCenter.clientY - contentCenterY - state.pinchStart.anchorY * state.scale;
      applyTransform(state);
      return;
    }

    if (state.points.size === 1 && state.dragStart && state.scale > 1.01) {
      state.translateX = state.dragStart.translateX + (event.clientX - state.dragStart.clientX);
      state.translateY = state.dragStart.translateY + (event.clientY - state.dragStart.clientY);
      applyTransform(state);
    }
  };

  const finishPointer = (state, event, cancelled = false) => {
    if (!state.active) return;
    event.stopPropagation();
    if (!state.points.has(event.pointerId)) return;

    event.preventDefault();

    const pointer = state.points.get(event.pointerId);
    const wasSinglePointer = state.points.size === 1;
    const dragStart = state.dragStart;
    const deltaX = dragStart ? event.clientX - dragStart.clientX : 0;
    const deltaY = dragStart ? event.clientY - dragStart.clientY : 0;
    const travel = Math.hypot(deltaX, deltaY);

    state.points.delete(event.pointerId);

    if (state.points.size === 1) {
      const remainingPoint = Array.from(state.points.values())[0];
      state.dragStart = {
        clientX: remainingPoint.clientX,
        clientY: remainingPoint.clientY,
        translateX: state.translateX,
        translateY: state.translateY,
        time: Date.now(),
      };
      state.pinchStart = null;
      return;
    }

    if (state.points.size > 1) return;

    state.dragStart = null;
    state.pinchStart = null;

    if (cancelled) {
      state.wasPinching = false;
      return;
    }

    if (state.wasPinching) {
      state.wasPinching = false;
      if (state.scale <= 1.01) resetTransform(state);
      applyTransform(state);
      requestSharperCandidate(state);
      state.liveStatus.textContent = `Zoom ${Math.round(state.scale * 100)}%`;
      return;
    }

    if (!wasSinglePointer) return;

    if (pointer.pointerType !== 'mouse' && travel < 14) {
      const now = Date.now();
      const tapGap = now - state.lastTap.time;
      const tapDistance = Math.hypot(event.clientX - state.lastTap.clientX, event.clientY - state.lastTap.clientY);

      if (tapGap > 0 && tapGap < DOUBLE_TAP_DELAY && tapDistance < 36) {
        state.lastTap = { time: 0, clientX: 0, clientY: 0 };
        toggleZoomAt(state, event.clientX, event.clientY);
        return;
      }

      state.lastTap = { time: now, clientX: event.clientX, clientY: event.clientY };
    }

    if (
      state.scale <= 1.01 &&
      Math.abs(deltaX) >= SWIPE_THRESHOLD &&
      Math.abs(deltaX) > Math.abs(deltaY) * 1.2
    ) {
      showImage(state, state.index + (deltaX < 0 ? 1 : -1));
      return;
    }

    if (state.scale <= 1.01) resetTransform(state);
    applyTransform(state);
  };

  const createControls = (state) => {
    const controls = document.createElement('div');
    controls.className = 'ym-media-inspector__controls';

    const previousButton = createButton(
      'ym-media-inspector__nav ym-media-inspector__nav--previous',
      strings.previous,
      arrowIcon('previous'),
    );
    const nextButton = createButton(
      'ym-media-inspector__nav ym-media-inspector__nav--next',
      strings.next,
      arrowIcon('next'),
    );
    const counter = document.createElement('output');
    counter.className = 'ym-media-inspector__counter';
    counter.setAttribute('aria-hidden', 'true');

    const liveStatus = document.createElement('p');
    liveStatus.className = 'visually-hidden';
    liveStatus.setAttribute('aria-live', 'polite');
    liveStatus.setAttribute('aria-atomic', 'true');

    const keyboardInstructions = document.createElement('p');
    keyboardInstructions.id = `YMMediaInstructions-${getSectionSuffix(state.modal)}`;
    keyboardInstructions.className = 'visually-hidden';
    keyboardInstructions.textContent = strings.keyboardHint;

    const hint = document.createElement('p');
    hint.className = 'ym-media-inspector__hint';
    hint.setAttribute('aria-hidden', 'true');
    hint.innerHTML = `${magnifierIcon()}<span></span>`;
    hint.querySelector('span').textContent = strings.pinchHint;

    const zoomControls = document.createElement('div');
    zoomControls.className = 'ym-media-inspector__zoom-controls';
    const zoomOutButton = createButton('ym-media-inspector__zoom-button', strings.zoomOut, '−');
    const zoomResetButton = createButton(
      'ym-media-inspector__zoom-button ym-media-inspector__zoom-reset',
      strings.resetZoom,
      '100%',
    );
    const zoomInButton = createButton('ym-media-inspector__zoom-button', strings.zoomIn, '+');
    zoomControls.append(zoomOutButton, zoomResetButton, zoomInButton);

    controls.append(
      previousButton,
      nextButton,
      counter,
      hint,
      zoomControls,
      liveStatus,
      keyboardInstructions,
    );
    state.dialog.appendChild(controls);

    state.controls = controls;
    state.previousButton = previousButton;
    state.nextButton = nextButton;
    state.counter = counter;
    state.hint = hint;
    state.liveStatus = liveStatus;
    state.keyboardInstructions = keyboardInstructions;
    state.zoomControls = zoomControls;
    state.zoomOutButton = zoomOutButton;
    state.zoomResetButton = zoomResetButton;
    state.zoomInButton = zoomInButton;
    setZoomControlsEnabled(state, false);

    controls.addEventListener('pointerup', (event) => event.stopPropagation());
    previousButton.addEventListener('click', () => showImage(state, state.index - 1));
    nextButton.addEventListener('click', () => showImage(state, state.index + 1));
    zoomOutButton.addEventListener('click', () => {
      if (state.scale > 1.01) setKeyboardZoom(state, state.scale - 0.5);
    });
    zoomResetButton.addEventListener('click', () => {
      if (state.scale > 1.01) setKeyboardZoom(state, 1);
    });
    zoomInButton.addEventListener('click', () => {
      if (state.scale < MAX_SCALE - 0.01) setKeyboardZoom(state, state.scale + 0.5);
    });
    updateCounter(state);
    updateZoomControls(state);
  };

  const largestImageCandidate = (image) => imageCandidates(image).at(-1)?.url || image.currentSrc || image.src;

  const addDesktopLens = (opener) => {
    if (opener.dataset.ymMediaLensReady === 'true') return;

    const image = opener.querySelector('img');
    if (!image) return;

    opener.dataset.ymMediaLensReady = 'true';
    const lens = document.createElement('span');
    lens.className = 'ym-media-inspector__lens';
    lens.setAttribute('aria-hidden', 'true');
    opener.appendChild(lens);

    const hideLens = () => lens.classList.remove('is-visible');

    const positionLens = (event) => {
      const openerRect = opener.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      const imageX = clamp(event.clientX - imageRect.left, 0, imageRect.width);
      const imageY = clamp(event.clientY - imageRect.top, 0, imageRect.height);
      const zoomRatio = 2.5;
      const lensCenterX = lens.clientWidth / 2;
      const lensCenterY = lens.clientHeight / 2;

      lens.style.left = `${imageRect.left - openerRect.left + imageX}px`;
      lens.style.top = `${imageRect.top - openerRect.top + imageY}px`;
      lens.style.backgroundSize = `${imageRect.width * zoomRatio}px ${imageRect.height * zoomRatio}px`;
      lens.style.backgroundPosition = `${lensCenterX - imageX * zoomRatio}px ${lensCenterY - imageY * zoomRatio}px`;
    };

    opener.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      lens.style.backgroundImage = `url("${largestImageCandidate(image).replaceAll('"', '\\"')}")`;
      lens.classList.add('is-visible');
      positionLens(event);
    });

    opener.addEventListener('pointermove', (event) => {
      if (!lens.classList.contains('is-visible')) return;
      positionLens(event);
    });

    opener.addEventListener('pointerleave', hideLens);
    opener.addEventListener('pointercancel', hideLens);
  };

  const enhanceGallery = (state) => {
    const gallery = getGallery(state.modal);
    if (!gallery) return;

    const imageOpeners = Array.from(gallery.querySelectorAll('.product__modal-opener--image'));
    imageOpeners.forEach(addDesktopLens);

    const firstOpener = imageOpeners[0];
    if (firstOpener && !gallery.querySelector('.ym-media-inspector__item-hint')) {
      const itemHint = document.createElement('span');
      itemHint.className = 'ym-media-inspector__item-hint';
      itemHint.setAttribute('aria-hidden', 'true');
      itemHint.innerHTML = `${magnifierIcon()}<span></span>`;
      itemHint.querySelector('span').textContent = strings.itemHint;
      firstOpener.appendChild(itemHint);
    }

    if (state.observedGalleries.has(gallery)) return;
    state.observedGalleries.add(gallery);

    gallery.addEventListener(
      'click',
      (event) => {
        const opener = event.target.closest('.product__modal-opener');
        if (!opener || !gallery.contains(opener) || state.active) return;

        if (!opener.classList.contains('product__modal-opener--image')) {
          state.modal.classList.remove(MODAL_CLASS);
          setZoomControlsEnabled(state, false);
          state.previousButton.hidden = true;
          state.nextButton.hidden = true;
          state.previousButton.disabled = true;
          state.nextButton.disabled = true;
          return;
        }

        const mediaId = opener.querySelector('.product__media-toggle[data-media-id]')?.dataset.mediaId;
        if (!refreshImages(state, mediaId)) {
          state.modal.classList.remove(MODAL_CLASS);
          setZoomControlsEnabled(state, false);
          state.previousButton.hidden = true;
          state.nextButton.hidden = true;
          state.previousButton.disabled = true;
          state.nextButton.disabled = true;
          return;
        }

        // Prepare controls before Rise calculates its focus trap in ModalDialog.show().
        setZoomControlsEnabled(state, true);
        state.modal.classList.add(MODAL_CLASS);
      },
      true,
    );

    const galleryObserver = new MutationObserver(() => {
      if (state.galleryRefreshFrame !== null) {
        window.cancelAnimationFrame(state.galleryRefreshFrame);
      }

      state.galleryRefreshFrame = window.requestAnimationFrame(() => {
        state.galleryRefreshFrame = null;
        if (!gallery.isConnected) return;

        enhanceGallery(state);
        if (!state.active) refreshImages(state);
      });
    });
    galleryObserver.observe(gallery, { childList: true, subtree: true });
    state.galleryObservers.set(gallery, galleryObserver);
  };

  const activate = (state) => {
    state.images = getImages(state.modal, state.content);
    const activeImage = state.content.querySelector(':scope > img[data-media-id].active');
    const activeImageIndex = state.images.indexOf(activeImage);
    if (!activeImage || activeImageIndex < 0 || !state.images.length) return;

    state.active = true;
    state.index = activeImageIndex;
    state.modal.classList.add(MODAL_CLASS);
    document.body.classList.add(OPEN_BODY_CLASS);
    const describedBy = new Set((state.originalDescribedBy || '').split(/\s+/).filter(Boolean));
    describedBy.add(state.keyboardInstructions.id);
    state.dialog.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
    showImage(state, state.index);

    state.hint.classList.remove('is-visible');
    if (state.hintFrame !== null) window.cancelAnimationFrame(state.hintFrame);
    state.hintFrame = window.requestAnimationFrame(() => {
      state.hintFrame = null;
      if (state.active && state.modal.hasAttribute('open')) state.hint.classList.add('is-visible');
    });
    window.clearTimeout(state.hintTimer);
    state.hintTimer = window.setTimeout(() => state.hint.classList.remove('is-visible'), 5000);
  };

  const deactivate = (state) => {
    if (!state.active) return;

    state.active = false;
    window.clearTimeout(state.hintTimer);
    if (state.hintFrame !== null) window.cancelAnimationFrame(state.hintFrame);
    if (state.imageFrame !== null) window.cancelAnimationFrame(state.imageFrame);
    state.hintFrame = null;
    state.imageFrame = null;
    state.lastTap = { time: 0, clientX: 0, clientY: 0 };
    state.hint.classList.remove('is-visible');
    restoreImageSizes(state);
    resetTransform(state);
    state.images.forEach((image) => image.removeAttribute('aria-hidden'));
    state.modal.classList.remove(MODAL_CLASS);
    document.body.classList.remove(OPEN_BODY_CLASS);
    setZoomControlsEnabled(state, false);
    if (state.originalDescribedBy === null) state.dialog.removeAttribute('aria-describedby');
    else state.dialog.setAttribute('aria-describedby', state.originalDescribedBy);
  };

  const enhanceModal = (modal) => {
    if (modalStates.has(modal)) return;

    const dialog = modal.querySelector('.product-media-modal__dialog[role="dialog"]');
    const content = modal.querySelector('.product-media-modal__content[role="document"]');
    const images = content ? getImages(modal, content) : [];
    if (!dialog || !content || !images.length) return;

    if (!dialog.hasAttribute('aria-label') && !dialog.hasAttribute('aria-labelledby')) {
      dialog.setAttribute('aria-label', strings.dialog);
    }

    const state = {
      modal,
      dialog,
      content,
      images,
      active: false,
      index: 0,
      scale: 1,
      translateX: 0,
      translateY: 0,
      points: new Map(),
      dragStart: null,
      pinchStart: null,
      wasPinching: false,
      lastTap: { time: 0, clientX: 0, clientY: 0 },
      originalSizes: new WeakMap(),
      requestedCandidateWidths: new WeakMap(),
      sizedImages: new Set(),
      originalDescribedBy: dialog.getAttribute('aria-describedby'),
      hintTimer: null,
      hintFrame: null,
      imageFrame: null,
      galleryRefreshFrame: null,
      contentRefreshFrame: null,
      observedGalleries: new WeakSet(),
      galleryObservers: new WeakMap(),
    };

    modalStates.set(modal, state);
    createControls(state);
    enhanceGallery(state);

    content.addEventListener('pointerdown', (event) => pointerDown(state, event));
    content.addEventListener('pointermove', (event) => pointerMove(state, event));
    content.addEventListener('pointerup', (event) => finishPointer(state, event));
    content.addEventListener('pointercancel', (event) => finishPointer(state, event, true));
    content.addEventListener('lostpointercapture', (event) => finishPointer(state, event, true));
    content.addEventListener('dblclick', (event) => {
      if (!state.active) return;
      event.preventDefault();
      event.stopPropagation();
      toggleZoomAt(state, event.clientX, event.clientY);
    });

    modal.addEventListener('keydown', (event) => {
      if (!state.active || event.altKey || event.ctrlKey || event.metaKey) return;

      if (event.key === '+' || event.key === '=' || event.key === 'Add') {
        event.preventDefault();
        setKeyboardZoom(state, state.scale + 0.5);
        return;
      }

      if (event.key === '-' || event.key === '_' || event.key === 'Subtract') {
        event.preventDefault();
        setKeyboardZoom(state, state.scale - 0.5);
        return;
      }

      if (event.key === '0') {
        event.preventDefault();
        setKeyboardZoom(state, 1);
        return;
      }

      if (state.scale > 1.01 && event.shiftKey && event.key.startsWith('Arrow')) {
        event.preventDefault();
        const panStep = 64;
        if (event.key === 'ArrowLeft') state.translateX += panStep;
        if (event.key === 'ArrowRight') state.translateX -= panStep;
        if (event.key === 'ArrowUp') state.translateY += panStep;
        if (event.key === 'ArrowDown') state.translateY -= panStep;
        applyTransform(state);
        state.liveStatus.textContent = `Zoom ${Math.round(state.scale * 100)}%, image panned`;
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showImage(state, state.index - 1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showImage(state, state.index + 1);
      }
    });

    const openObserver = new MutationObserver(() => {
      if (modal.hasAttribute('open')) activate(state);
      else deactivate(state);
    });
    openObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });

    const contentObserver = new MutationObserver(() => {
      if (state.contentRefreshFrame !== null) {
        window.cancelAnimationFrame(state.contentRefreshFrame);
      }

      state.contentRefreshFrame = window.requestAnimationFrame(() => {
        state.contentRefreshFrame = null;
        if (state.active) return;

        refreshImages(state);
        enhanceGallery(state);
      });
    });
    contentObserver.observe(content, { childList: true });

    if (modal.hasAttribute('open')) activate(state);
  };

  const initialize = () => {
    document.querySelectorAll('product-modal.product-media-modal').forEach(enhanceModal);
  };

  const bodyObserver = new MutationObserver(initialize);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialize();
      bodyObserver.observe(document.body, { childList: true });
    });
  } else {
    initialize();
    bodyObserver.observe(document.body, { childList: true });
  }

  document.addEventListener('shopify:section:load', initialize);
  window.addEventListener('resize', () => {
    document.querySelectorAll(`product-modal.${MODAL_CLASS}`).forEach((modal) => {
      const state = modalStates.get(modal);
      if (state?.active) {
        applyTransform(state);
        requestSharperCandidate(state);
      }
    });
  });
})();
